import { FormRow } from '@creditornot/cb-form';
import { ModalHeader } from '@creditornot/cbt-modal';
import { useMutation } from '@tanstack/react-query';
import React, { useCallback, useContext, useMemo } from 'react';
import { Field, Form } from 'react-final-form';
import toast from 'react-hot-toast';
import { FormattedMessage, useIntl } from 'react-intl';

import { bulkUpdateAvailabilityPeriodWoltSpecials } from 'apis/restaurant-api/merchant-admin/menu2/api';
import { BulkUpdateSpecialsPeriodPayload, MenuContainer } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { Item } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { RFFFieldErrorWrapper } from 'common/components';
import validateYupSchema from 'common/utils/validateYupSchema';
import { MenuLocaleContext } from 'modules/menu/MenuLocaleContext';
import useMenuData from 'modules/menu/useMenuData';

import AvailabilityInputs from './AvailabilityInputs';
import { CancelButton, Content, Footer, Root, SubmitButton } from './styled';
import {
  asLocaleNaiveDate,
  fromFormValuesToBatchUpdateAvailabilityPeriodPayload,
  isErrorResponseWithDetails,
} from './utils';
import { bulkUpdatePeriodSpecialsValidationSchema } from './validation';
import BulkActionModalTitle from '../BulkActionModalTitle';

type Props = {
  menuId: string;
  onClearItemSelection: () => void;
  onClose: () => void;
  selectedItemIds: Set<string>;
};

type Sorting = (item: Item) => any;

export type BulkUpdateSpecialsPeriodFormValues = {
  availabilityEnd: number | undefined;
  availabilityStart: number | undefined;
  previousAvailabilityEnd: number | undefined;
};

const ToastMessage = ({ size, success }: { size: number; success: boolean }) => {
  const key = success
    ? 'menu.bulkActions.updateWoltSpecials.successDescription'
    : 'menu.bulkActions.updateWoltSpecials.errorDescription';
  return <FormattedMessage id={key} values={{ count: size }} />;
};

const UpdateSuccess = ({ size }: { size: number }) => <ToastMessage success size={size} />;
const UpdateError = ({ size }: { size: number }) => <ToastMessage success={false} size={size} />;

const BulkUpdateSpecialsModalAvailabilityPeriod = ({
  menuId,
  onClose,
  onClearItemSelection,
  selectedItemIds,
}: Props) => {
  const intl = useIntl();
  const { items, updateMenuData } = useMenuData();
  const { timezone } = useContext(MenuLocaleContext);

  const getGreatestPropertyOfWoltSpecialsItem: <U extends Sorting>(key: U) => ReturnType<U> = useCallback(
    key =>
      items
        .filter(item => selectedItemIds.has(item.id))
        .map(key)
        .sort((left, right) => (left > right ? -1 : 1))[0],
    [items, selectedItemIds],
  );

  const greatestAvailabilityPeriodEnd = useMemo(
    () => getGreatestPropertyOfWoltSpecialsItem((item: Item) => item.availability_period?.end?.$date ?? 0),
    [getGreatestPropertyOfWoltSpecialsItem],
  );

  const greatestAvailabilityPeriodStart = useMemo(
    () => getGreatestPropertyOfWoltSpecialsItem((item: Item) => item.availability_period?.start?.$date ?? 0),
    [getGreatestPropertyOfWoltSpecialsItem],
  );

  const initialFormValues: BulkUpdateSpecialsPeriodFormValues = {
    previousAvailabilityEnd: greatestAvailabilityPeriodEnd,
    availabilityStart: greatestAvailabilityPeriodStart,
    availabilityEnd: undefined,
  };

  const handleUpdateSpecials = useMutation(
    (payload: BulkUpdateSpecialsPeriodPayload) => bulkUpdateAvailabilityPeriodWoltSpecials(menuId, payload),
    {
      onSuccess: (data: MenuContainer) => {
        updateMenuData({ actionType: 'updateMenuContainer', payload: data });
        onClearItemSelection();
        toast.success(<UpdateSuccess size={selectedItemIds.size} />);
      },
      onError: (error: any) => {
        if (isErrorResponseWithDetails(error) && error?.response) {
          const errorCount = Object.keys(error.response?.data.error.items_errors).length;
          const successCount = selectedItemIds.size - errorCount;

          if (successCount > 0) {
            toast.success(<UpdateSuccess size={successCount} />);
          }

          if (errorCount > 0) {
            toast.error(<UpdateError size={errorCount} />, { duration: Infinity });
          }

          onClearItemSelection();
          updateMenuData({ actionType: 'updateMenuContainer', payload: error.response.data.modified_menu_container });
        } else {
          toast.error(<UpdateError size={selectedItemIds.size} />, { duration: Infinity });
        }
      },
      onSettled: onClose,
    },
  );
  const nowTimestamp = new Date().getTime();
  const minimumDate = nowTimestamp > greatestAvailabilityPeriodStart ? nowTimestamp : greatestAvailabilityPeriodStart;
  const beforeDate = asLocaleNaiveDate(minimumDate, timezone);
  const disabledDays = greatestAvailabilityPeriodStart > 0 ? { before: beforeDate } : undefined;

  return (
    <Root isOpen>
      <ModalHeader onCloseClick={onClose}>
        <BulkActionModalTitle
          count={selectedItemIds.size}
          title={<FormattedMessage id="menu.bulkActions.updateWoltSpecials.header" />}
        />
      </ModalHeader>
      <Form<BulkUpdateSpecialsPeriodFormValues>
        initialValues={initialFormValues}
        onSubmit={values => {
          const validValues = bulkUpdatePeriodSpecialsValidationSchema.validateSync(values, {
            context: { greatestAvailabilityPeriodStart },
          });
          const payload = fromFormValuesToBatchUpdateAvailabilityPeriodPayload(validValues, selectedItemIds);

          handleUpdateSpecials.mutate(payload);
        }}
        validate={validateYupSchema(bulkUpdatePeriodSpecialsValidationSchema, {
          greatestAvailabilityPeriodStart,
        })}
      >
        {({ handleSubmit, hasValidationErrors }) => (
          <form onSubmit={handleSubmit}>
            <Content>
              <FormRow
                label={intl.formatMessage({ id: 'special.availability-picker-previous-ending-label' })}
                horizontalPadding
              >
                {greatestAvailabilityPeriodEnd > 0 ? (
                  <Field<BulkUpdateSpecialsPeriodFormValues['previousAvailabilityEnd']> name="previousAvailabilityEnd">
                    {field => (
                      <RFFFieldErrorWrapper field={field} showErrorImmediately>
                        {input => <AvailabilityInputs disabled {...input} />}
                      </RFFFieldErrorWrapper>
                    )}
                  </Field>
                ) : (
                  '-'
                )}
              </FormRow>
              <FormRow label={intl.formatMessage({ id: 'special.availability-picker-ending-label' })} horizontalPadding>
                <Field<BulkUpdateSpecialsPeriodFormValues['availabilityEnd']> name="availabilityEnd">
                  {field => (
                    <RFFFieldErrorWrapper field={field} showErrorImmediately>
                      {input => (
                        <AvailabilityInputs
                          disabledDays={disabledDays}
                          dateHint={intl.formatMessage(
                            { id: 'menu.bulkActions.updateWoltSpecials.minDate' },
                            { date: beforeDate.toLocaleDateString() },
                          )}
                          {...input}
                        />
                      )}
                    </RFFFieldErrorWrapper>
                  )}
                </Field>
              </FormRow>
            </Content>
            <Footer>
              <CancelButton onClick={onClose}>
                <FormattedMessage id="state.cancel" />
              </CancelButton>
              <SubmitButton
                type="submit"
                onClick={handleSubmit}
                disabled={handleUpdateSpecials.isLoading || hasValidationErrors}
                loading={handleUpdateSpecials.isLoading}
              >
                <FormattedMessage id="state.save" />
              </SubmitButton>
            </Footer>
          </form>
        )}
      </Form>
    </Root>
  );
};

export default BulkUpdateSpecialsModalAvailabilityPeriod;
